import React, { useState, useEffect } from 'react';
import { Sliders, Xmark, CircleArrowUp, Play, ArrowShapeDownToLine, ChevronDown, ChevronUp, ArrowLeft } from "@gravity-ui/icons";
import AdvancedEditAudioPlayer from '../../components/AdvancedEditAudioPlayer';
import { useLocalState } from '../../contexts/LocalStateProvider';
import { useLocation } from 'react-router-dom';
import apiService from '../../api/apiService';
import Toast from '../generic_components/Toast';
import { getCookie } from '../../utils/cookieUtils';
import { usePostHog } from "posthog-js/react";
import * as Sentry from "@sentry/react"
import { getPartitionedLocalStorage } from '../../utils/tokenExtraction';
// List of all suggestions
const allSuggestions = [
  "Trim start, fade-in",
  "Boost volume",
  "Add reverb",
  "Fade-out ending",
  "Normalize levels",
  "Add delay",
  "Trim silent parts",
  "Add compression",
  "EQ adjustment",
  "Volume up",
  "Bass boost",
  "Add echo",
  "Smooth transitions",
  "De-noise",
];

// Map suggestions to edit operations for API
const suggestionToEditOperations = {
  "Trim start, fade-in": [
    { operation_type: "trim_start", parameters: { amount: 0.5 } },
    { operation_type: "fade_in", parameters: { duration: 1.0 } }
  ],
  "Boost volume": [
    { operation_type: "gain_adjust", parameters: { amount: 3.0 } }
  ],
  "Add reverb": [
    { operation_type: "add_reverb", parameters: { mix: 0.3, room_size: 0.5 } }
  ],
  "Fade-out ending": [
    { operation_type: "fade_out", parameters: { duration: 3.0 } }
  ],
  "Normalize levels": [
    { operation_type: "normalize", parameters: {} }
  ],
  "Add delay": [
    { operation_type: "add_delay", parameters: { mix: 0.2, time: 0.25 } }
  ],
  "Trim silent parts": [
    { operation_type: "trim_start", parameters: { amount: 0.2 } },
    { operation_type: "trim_end", parameters: { amount: 0.2 } }
  ],
  "Add compression": [
    { operation_type: "add_compression", parameters: { threshold: -20, ratio: 4 } }
  ],
  "EQ adjustment": [
    { operation_type: "add_eq", parameters: { low: 2, mid: 0, high: 1 } }
  ],
  "Volume up": [
    { operation_type: "gain_adjust", parameters: { amount: 2.0 } }
  ],
  "Bass boost": [
    { operation_type: "add_eq", parameters: { low: 3, mid: 0, high: 0 } }
  ],
  "Add echo": [
    { operation_type: "add_delay", parameters: { mix: 0.3, time: 0.4 } }
  ],
  "Vocal enhancement": [
    { operation_type: "add_eq", parameters: { low: -1, mid: 2, high: 1 } },
    { operation_type: "add_compression", parameters: { threshold: -20, ratio: 2.5 } }
  ],
  "Smooth transitions": [
    { operation_type: "fade_in", parameters: { duration: 0.5 } },
    { operation_type: "fade_out", parameters: { duration: 1.5 } }
  ],
  "De-noise": [
    { operation_type: "noise_reduction", parameters: { amount: 0.5 } }
  ],
};


// Function to shuffle array and get first N elements
const getRandomSuggestions = (arr, n) => {
  const shuffled = [...arr].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, n);
};

const AdvancedEditModal = ({ onClose, initialAudioUri, projectId: propProjectId, responseId: propResponseId }) => {
  const [inputValue, setInputValue] = useState('');
  const [displayedSuggestions, setDisplayedSuggestions] = useState([]);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [originalAudioUrl, setOriginalAudioUrl] = useState(null);
  const [editedAudioUrl, setEditedAudioUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info');
  const { localState } = useLocalState();
  const location = useLocation();
  const posthog = usePostHog();
  
  // New state for selected tags
  const [selectedTags, setSelectedTags] = useState([]);
  
  // State for waveform changes to highlight in edited audio
  const [waveformChanges, setWaveformChanges] = useState(null);
  
  // State to track if edited audio is fully loaded and ready for download
  const [isEditedAudioReady, setIsEditedAudioReady] = useState(false);
  const [fileType, setFileType] = useState('WAV');
  const [isDownloading, setIsDownloading] = useState(false);

  // Extract projectId from URL query params
  const getProjectId = () => {
    const params = new URLSearchParams(location.search);
    return propProjectId || params.get('projectId');
  };

  const userId = localState?.userProfileData?.user_id || getCookie("user_id") || getPartitionedLocalStorage("user_id");
  const projectId = getProjectId();

  // Reset modal state completely to defaults
  const resetModalState = () => {
    posthog.capture("Click-GoBack-AdvanceEditModal",{is_user_logged_in: true})
    setInputValue('');
    setSelectedTags([]);
    setEditedAudioUrl(null);
    setWaveformChanges(null);
    setShowOriginal(true);
    setIsProcessing(false);
    setIsEditedAudioReady(false);
    setFileType('WAV'); // Reset file type selection
    setIsDownloading(false);
    
    // Clear any toast messages
    if (toastMessage) {
      setToastMessage('');
    }
    
    console.log("[AdvancedEdit] Modal state reset");
  };
  
  // Handle modal close with reset
  const handleClose = () => {
    resetModalState();
    onClose();
  };
  
  // Handle cancel button
  const handleCancel = () => {
    // If editing is in progress, show confirmation
    if (isProcessing) {
      if (window.confirm("Audio processing is in progress. Are you sure you want to cancel?")) {
        resetModalState();
        onClose();
      }
    } else {
      resetModalState();
      onClose();
    }
  };
  
  // Use effect to reset modal when it's opened
  useEffect(() => {
    if (!isProcessing) {
      resetModalState();
      setDisplayedSuggestions(showAllSuggestions ? allSuggestions : getRandomSuggestions(allSuggestions, 10));

      if (initialAudioUri) {
        console.log("[AdvancedEdit] Using initialAudioUri from props:", initialAudioUri);
        setOriginalAudioUrl({
          gcsUrl: initialAudioUri,
          webUrl: convertGcsToWebUrl(initialAudioUri)
        });
      } else if (userId && projectId) {
        console.log("[AdvancedEdit] initialAudioUri not provided. userId and projectId FOUND, calling fetchLatestTrack");
        fetchLatestTrack();
      } else {
        console.log("[AdvancedEdit] initialAudioUri not provided. Waiting for userId and/or projectId to be available to fetch latest track.");
      }
    }
  }, [userId, projectId, localState?.userProfileData, showAllSuggestions, initialAudioUri]);

  const fetchLatestTrack = async () => {
    if (!projectId) {
      console.error("[AdvancedEdit] fetchLatestTrack called without projectId", { projectId });
      return;
    }
    
    const userIdToUse = userId || getCookie("user_id") || getPartitionedLocalStorage("user_id");
    if (!userIdToUse) {
      console.error("[AdvancedEdit] No user ID available from state or cookies");
      return;
    }
    
    console.log("[AdvancedEdit] Fetching latest track for project:", projectId, "with user ID:", userIdToUse);
    
    try {
      const response = await apiService.sendRequest('getLatestProjectTrack', {
        project_id: projectId,
        user_id: userIdToUse
      });
      
      console.log("[AdvancedEdit] Latest track fetch response:", response);
      
      if (response.success) {
        let audioUrl = response.audioUrl;
        
        if (Array.isArray(audioUrl)) {
          console.log("[AdvancedEdit] Audio URL is an array, extracting first item for fetchLatestTrack");
          audioUrl = audioUrl[0];
        }
        
        console.log("[AdvancedEdit] Original GCS URL from fetchLatestTrack:", audioUrl);
        
        setOriginalAudioUrl({
          gcsUrl: audioUrl,
          webUrl: convertGcsToWebUrl(audioUrl)
        });
      } else {
        console.error("[AdvancedEdit] Failed to fetch latest track:", response.error);
        setToastMessage("Unable to load audio track");
        setToastType("error");
      }
    } catch (error) {
      console.error("[AdvancedEdit] Error fetching latest track:", error);
      Sentry.captureException(error);
      setToastMessage("Failed to connect to server");
      setToastType("error");
    }
  };

  // Helper function to convert GCS URL to web-accessible URL via proxy endpoint
  const convertGcsToWebUrl = (gcsUrl) => {
    if (!gcsUrl) return null;
    
    console.log("[AdvancedEdit] Creating proxy URL for:", gcsUrl);
    
    // Extract the file name from the GCS URL
    const fileName = gcsUrl.split('/').pop();
    
    // Use the server endpoint to proxy the audio instead of direct GCS access
    // This solves CORS issues and permissions problems
    const proxyUrl = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/proxy-audio?gcs_url=${encodeURIComponent(gcsUrl)}`;
    
    console.log("[AdvancedEdit] Created proxy URL:", proxyUrl);
    return proxyUrl;
  };

  const handleToggleSuggestions = () => {
    posthog.capture("Click-ShowLess/More-Edit-AdvanceEditModal",{is_user_logged_in: true})
    if (showAllSuggestions) {
      setDisplayedSuggestions(getRandomSuggestions(allSuggestions, 10));
    } else {
      setDisplayedSuggestions(allSuggestions);
    }
    setShowAllSuggestions(!showAllSuggestions);
  };

  // Handle tag selection
  const handleTagSelect = (tag) => {
    if (selectedTags.length >= 3) {
      // Show toast message if user tries to add more than 3 tags
      setToastMessage("Maximum 3 effects allowed");
      setToastType("warning");
      return;
    }
    
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
    }
    setInputValue('');
  };
  
  // Handle tag removal
  const handleRemoveTag = (tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };
  
  // Handle input change - will be used for filtering suggestions
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };
  
  // Handle key press in input field
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      // If the input matches a suggestion, add it as a tag
      const matchingSuggestion = allSuggestions.find(
        suggestion => suggestion.toLowerCase() === inputValue.trim().toLowerCase()
      );
      
      if (matchingSuggestion) {
        handleTagSelect(matchingSuggestion);
      } else if (!selectedTags.includes(inputValue.trim())) {
        // Otherwise add the custom input as a tag
        setSelectedTags([...selectedTags, inputValue.trim()]);
        setInputValue('');
      }
    }
  };
  
  // Process the audio edit when applying effects
  const handleApplyEffects = async () => {
    posthog.capture("Click-ApplyEffect-AdvanceEditModal",{is_user_logged_in: true});
    console.log("[AdvancedEdit] Starting edit process with tags:", selectedTags);
    
    const userIdToUse = userId || getCookie("user_id") || getPartitionedLocalStorage("user_id");
    if (!userIdToUse) {
      console.error("[AdvancedEdit] No user ID available for processing edit");
      setToastMessage("User authentication issue. Please reload the page.");
      setToastType("error");
      return;
    }
    
    if (selectedTags.length === 0) {
      console.log("[AdvancedEdit] No tags selected, showing warning");
      setToastMessage("Please select at least one effect");
      setToastType("warning");
      return;
    }
    
    if (!originalAudioUrl || !originalAudioUrl.gcsUrl) {
      console.error("[AdvancedEdit] No original audio GCS URL available. initialAudioUri prop might be missing or fetchLatestTrack failed.");
      setToastMessage("No audio track available for editing.");
      setToastType("error");
      return;
    }
    
    if (editedAudioUrl) {
      console.log("[AdvancedEdit] Clearing previous edit data before processing new edit");
      setEditedAudioUrl(null);
      setWaveformChanges(null);
    }
    
    setIsProcessing(true);
    
    try {
      let allEditOperations = [];
      const prompt = selectedTags.join(", ");
      
      selectedTags.forEach(tag => {
        const operations = suggestionToEditOperations[tag] || [];
        allEditOperations = [...allEditOperations, ...operations];
      });
      
      console.log("[AdvancedEdit] Combined edit operations:", allEditOperations);
      
      console.log("[AdvancedEdit] Using GCS URL for processing:", originalAudioUrl.gcsUrl);
      
      const gcsUrl = originalAudioUrl.gcsUrl;
      const originalMultiTrackResponseId = propResponseId;

      console.log(`[AdvancedEdit] Using originalMultiTrackResponseId for original_audio_id: ${originalMultiTrackResponseId}`);
      
      const uniqueRequestId = `req_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 5)}`;
      
      const payload = {
        prompt: prompt,
        audioUri: originalAudioUrl.gcsUrl,
        project_id: projectId,
        user_id: userIdToUse,
        edit_operations: allEditOperations,
        original_audio_id: originalMultiTrackResponseId || (gcsUrl.match(/\/(\d+)-[^\/]+\.wav$/) ? gcsUrl.match(/\/(\d+)-[^\/]+\.wav$/)[1] : null),
        request_id: uniqueRequestId
      };
      
      console.log("[AdvancedEdit] Sending edit request with payload:", payload);
      
      const response = await apiService.handleAdvancedAudioEdit(payload);
      
      console.log("[AdvancedEdit] Received edit response:", response);
      
      if (response.success) {
        const editedGcsUrl = response.editedAudioUrl;
        const editedWebUrl = convertGcsToWebUrl(editedGcsUrl);
        
        console.log("[AdvancedEdit] Setting edited audio URLs:", {
          gcs: editedGcsUrl, 
          web: editedWebUrl
        });
        
        setEditedAudioUrl({
          gcsUrl: editedGcsUrl,
          webUrl: editedWebUrl,
          originalAudioId: response.originalAudioId || (gcsUrl.match(/\/(\d+)-[^\/]+\.wav$/) ? gcsUrl.match(/\/(\d+)-[^\/]+\.wav$/)[1] : null)
        });
        
        generateWaveformChanges(allEditOperations);
        
        setShowOriginal(false);
        
        if (response.originalAudioId) {
          fetchAudioEdits(response.originalAudioId);
        }
        
        setToastMessage("Audio processed successfully");
        setToastType("success");
      } else {
        console.error("[AdvancedEdit] Failed to process audio:", response.error);
        setToastMessage(response.error || "Failed to process audio");
        setToastType("error");
      }
    } catch (error) {
      console.error("[AdvancedEdit] Error processing audio:", error);
      Sentry.captureException(error);
      setToastMessage("Failed to connect to server");
      setToastType("error");
    } finally {
      setIsProcessing(false);
      console.log("[AdvancedEdit] Edit processing completed");
    }
  };
  
  // Generate waveform changes based on edit operations
  const generateWaveformChanges = (operations) => {
    let changes = [];
    
    operations.forEach(op => {
      switch(op.operation_type) {
        case "trim_start":
          changes.push({
            start: 0,
            end: op.parameters.amount,
            color: "rgba(252, 16, 242, 1)",
            label: "Trim"
          });
          break;
        case "trim_end":
          changes.push({
            start: 0.9,
            end: 1.0,
            color: "rgba(252, 16, 242, 1)",
            label: "Trim"
          });
          break;
        case "fade_in":
          changes.push({
            start: 0,
            end: Math.min(op.parameters.duration * 0.2, 0.3),
            color: "rgba(48, 120, 228, 1)",
            label: "Fade-in"
          });
          break;
        case "fade_out":
          changes.push({
            start: Math.max(1.0 - (op.parameters.duration * 0.2), 0.7),
            end: 1.0,
            color: "rgba(48, 120, 228, 1)",
            label: "Fade-out"
          });
          break;
        case "add_eq":
          changes.push({
            start: 0,
            end: 1.0,
            color: "rgba(252, 143, 10, 1)",
            label: "EQ"
          });
          break;
        case "add_reverb":
          changes.push({
            start: 0,
            end: 1.0,
            color: "rgba(252, 143, 10, 1)",
            label: "Reverb"
          });
          break;
        case "add_delay":
          changes.push({
            start: 0,
            end: 1.0,
            color: "rgba(252, 143, 10, 1)",
            label: "Delay"
          });
          break;
        case "add_compression":
          changes.push({
            start: 0,
            end: 1.0,
            color: "rgba(252, 143, 10, 1)",
            label: "Compression"
          });
          break;
        case "noise_reduction":
          changes.push({
            start: 0,
            end: 1.0,
            color: "rgba(252, 143, 10, 1)",
            label: "De-noise"
          });
          break;
        case "gain_adjust":
          changes.push({
            start: 0,
            end: 1.0,
            color: "rgba(18, 183, 106, 1)",
            label: "Volume"
          });
          break;
        default:
          changes.push({
            start: 0,
            end: 1.0,
            color: "rgba(200, 200, 200, 1)",
            label: "Effect"
          });
      }
    });
    
    setWaveformChanges(changes);
    console.log("[AdvancedEdit] Generated waveform changes:", changes);
  };
  
  // Fetch all edits for an original audio
  const fetchAudioEdits = async (originalAudioId) => {
    if (!originalAudioId || !projectId || !userId) {
      console.log("[AdvancedEdit] Missing required data for fetching audio edits");
      return;
    }
    
    try {
      console.log(`[AdvancedEdit] Fetching edits for original audio ID: ${originalAudioId}`);
      
      const response = await apiService.getProjectAudioEdits(projectId, userId, originalAudioId);
      
      if (response.success && response.edits && response.edits.length > 0) {
        console.log(`[AdvancedEdit] Found ${response.edits.length} previous edits for this audio`);
      } else {
        console.log("[AdvancedEdit] No previous edits found for this audio");
      }
    } catch (error) {
      console.error("[AdvancedEdit] Error fetching audio edits:", error);
      Sentry.captureException(error);
    }
  };
  
  // Handle download of edited audio
  const handleDownload = async () => {
    posthog.capture("Click-Download-AdvanceEditModal", {is_user_logged_in: true});
    // Always use the edited audio if available, otherwise fallback to original
    const audioUrl = editedAudioUrl || originalAudioUrl;
    console.log("[AdvancedEdit] Downloading audio:", audioUrl);
    
    if (!audioUrl) {
      console.error("[AdvancedEdit] No audio URL available for download");
      setToastMessage("No audio available to download");
      setToastType("error");
      return;
    }
    
    setIsDownloading(true);
    
    try {
      let originalFileName = audioUrl.gcsUrl.split('/').pop() || `audio-${new Date().getTime()}.wav`;
      let fileName = originalFileName;
      
      if (audioUrl === editedAudioUrl) {
        const lastDotIndex = originalFileName.lastIndexOf('.');
        if (lastDotIndex !== -1) {
          fileName = originalFileName.substring(0, lastDotIndex) + 
                    " (Edited)" + 
                    originalFileName.substring(lastDotIndex);
        } else {
          fileName = originalFileName + " (Edited)";
        }
      }
      
      console.log("[AdvancedEdit] Downloading file as:", fileName, "File type:", fileType);
      
      // For MP3 downloads, use the API service
      if (fileType === "MP3") {
        // Extract audio ID using multiple methods to ensure we get it
        let audioId = null;
        
        // Method 1: Extract from originalAudioId property if available
        if (audioUrl.originalAudioId) {
          audioId = audioUrl.originalAudioId;
          console.log("[AdvancedEdit] Using originalAudioId property:", audioId);
        } 
        // Method 2: Try to extract from the filename using regex
        else {
          // Try different regex patterns to extract the ID from the GCS URL
          const patterns = [
            /\/(\d+)-[^\/]+\.wav$/,  // pattern like: /123-something.wav
            /\/(\d+)\.wav$/,         // pattern like: /123.wav
            /\/([a-zA-Z0-9_-]+)\.wav$/, // pattern for any alphanumeric ID
            /([^\/]+)\.wav$/         // fallback to extract anything before .wav
          ];
          
          // Try each pattern until we find a match
          for (const pattern of patterns) {
            const fileNameMatch = audioUrl.gcsUrl.match(pattern);
            if (fileNameMatch) {
              audioId = fileNameMatch[1];
              console.log("[AdvancedEdit] Extracted ID using regex pattern:", pattern, "ID:", audioId);
              break;
            }
          }
        }
        
        // Method 3: Parse the filename directly if no regex matches work
        if (!audioId) {
          const filename = audioUrl.gcsUrl.split('/').pop();
          const parts = filename.split('-');
          if (parts.length > 0) {
            audioId = parts[0];
            console.log("[AdvancedEdit] Extracted ID from filename parts:", audioId);
          }
        }
        
        // If we still don't have an ID, try to extract a numeric ID from the GCS URL
        if (!audioId) {
          const numericIdMatch = audioUrl.gcsUrl.match(/\/(\d+)/);
          if (numericIdMatch) {
            audioId = numericIdMatch[1];
            console.log("[AdvancedEdit] Extracted numeric ID from URL:", audioId);
          }
        }
        
        // Final check if we have an audio ID
        if (!audioId) {
          console.error("[AdvancedEdit] Could not extract audio ID for MP3 conversion");
          console.log("[AdvancedEdit] URL that failed:", audioUrl.gcsUrl);
          
          // Generate a fallback ID based on timestamp to allow the download to proceed
          audioId = `fallback_${Date.now()}`;
          console.log("[AdvancedEdit] Using fallback ID:", audioId);
          
          setToastMessage("Warning: Using temporary ID for MP3 download");
          setToastType("warning");
        }
        
        console.log("[AdvancedEdit] Using audio ID for MP3 download:", audioId);
        
        // Use the title without extension for the API
        const baseTitle = fileName.replace(/\.\w+$/, '');
        
        // Use the API service to handle MP3 conversion and download
        await apiService.downloadAudio(audioId, fileType, baseTitle);
        
        setToastMessage(`Successfully downloaded "${baseTitle}.mp3"`);
        setToastType("success");
      } 
      // For WAV downloads, use direct download
      else {
        // Use the existing proxy-audio endpoint which we know works
        const proxyUrl = audioUrl.webUrl;
        
        // Fetch the audio file as a blob
        const response = await fetch(proxyUrl);
        if (!response.ok) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }
        const blob = await response.blob();
        
        // Create a blob URL and trigger the download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName; // This forces download instead of navigation
        document.body.appendChild(a);
        a.click();
        
        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Show success message
        setToastMessage(`Successfully downloaded "${fileName}"`);
        setToastType("success");
      }
    } catch (error) {
      console.error("[AdvancedEdit] Error downloading file:", error);
      Sentry.captureException(error);
      setToastMessage("Failed to download file");
      setToastType("error");
    } finally {
      setIsDownloading(false);
    }
  };

  // Get filtered suggestions based on input
  const getFilteredSuggestions = () => {
    if (!inputValue.trim()) return displayedSuggestions;
    
    return displayedSuggestions.filter(
      suggestion => 
        suggestion.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedTags.includes(suggestion)
    );
  };

  return (
    <div
    className={`fixed inset-0 z-50 p-4 flex items-center justify-center`}
    style={{ background: 'rgba(0, 0, 0, 0.8)' }}
  >
    <div className="flex flex-col w-full max-w-3xl bg-[#1d1b1d] border border-solid border-[#3c393c] rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between pl-4 pb-2 pt-2 pr-2">
        <div className="flex items-center gap-2">
          <Sliders className="w-5 h-5" />
          <h2 className="text-lg font-normal">Advanced edit</h2>
        </div>
        <button
          onClick={handleClose}
          className="flex items-center justify-center hover:bg-white/5 rounded-full h-8 w-8 text-white/50 hover:text-white"
        >
          <Xmark className="w-4 h-4"/>
        </button>
      </div>
      <div className="w-full border-t border-t-[#2d2a2d]"></div>

      <div className="flex flex-col gap-3 p-4">
        {!editedAudioUrl && (
          <>
            <div className="flex flex-col gap-2">
              <div className="relative flex flex-wrap items-center gap-1 border border-white/10 rounded-[20px] min-h-10 p-1">
                {selectedTags.map((tag, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-full px-2 h-8 text-sm text-white"
                  >
                    <span>{tag}</span>
                    <button
                      onClick={() => {
                        posthog.capture("Click-CancelSelectedEdit-AdvanceEditModal",{is_user_logged_in: true})
                        handleRemoveTag(tag)
                      }}
                      className="text-white/40 hover:text-white"
                    >
                      <Xmark className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <input
                  type="text"
                  value=""
                  placeholder={
                    selectedTags.length === 0
                      ? "Click suggestions below to add effects (max 3)"
                      : selectedTags.length < 3
                        ? `Add ${3 - selectedTags.length} more effect${3 - selectedTags.length === 1 ? '' : 's'}...`
                        : "Maximum effects reached"
                  }
                  className="flex-grow bg-transparent border-none text-sm font-extralight mx-2 my-1.5 text-white placeholder-[#878188] focus:outline-none min-w-[150px]"
                  readOnly
                />
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <h4 className="text-sm font-extralight text-white/60">
                Suggested effects {selectedTags.length > 0 ? `(${selectedTags.length}/3)` : ''}
              </h4>
              <div className="flex flex-wrap gap-2">
                {displayedSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      posthog.capture("Click-SuggestedEdit-AdvanceEditModal",{is_user_logged_in: true})
                      handleTagSelect(suggestion);
                    }}
                    className={`bg-white/5  rounded-full px-2 h-7 text-xs text-white/40 transition-colors ${
                      selectedTags.includes(suggestion) || selectedTags.length >= 3 ? 'opacity-25 cursor-not-allowed' : 'hover:bg-white/10 hover:text-white'
                    }`}
                    disabled={selectedTags.includes(suggestion) || selectedTags.length >= 3}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
              {allSuggestions.length > 10 && (
                <button
                  onClick={handleToggleSuggestions}
                  className="flex text-xs text-white/40 gap-1 items-center px-2 h-7 rounded-full hover:bg-white/5 hover:text-white self-start transition-colors"
                >
                  {showAllSuggestions ? 'Show Less' : 'Show More'}
                  {showAllSuggestions ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
                </button>
              )}
            </div>

            {selectedTags.length > 0 && (
              <div className="flex justify-end">
                <div className={` h-9 flex rounded-full ${isProcessing ? 'bg-white/5 text-[#878188] cursor-not-allowed' : 'bg-gradient-to-r from-[#fc10f2] to-[#3078e4] text-white hover:[background:linear-gradient(135deg,#e509e4,#256bb8)] transition duration-300'}`}>
                  <button
                    className={`flex px-3 justify-center items-center  w-full rounded-full text-sm`}
                    onClick={handleApplyEffects}
                    disabled={isProcessing || !originalAudioUrl?.webUrl || selectedTags.length === 0}
                  >
                    {isProcessing ? (
                      'Processing...'
                    ) : (
                      <>
                        <img src="/MusicGenerateEnabled.svg" alt="" className="w-4 h-4 mr-2" />
                        Apply effects
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {editedAudioUrl && selectedTags.length > 0 && (
          <div className="flex flex-col gap-2">
            <h4 className="text-sm font-extralight text-white/80">Applied effects</h4>
            <div className="flex flex-wrap gap-2">
              {selectedTags.map((tag, index) => (
                <div
                  key={index}
                  className="border border-white/10 flex items-center justify-center rounded-full px-2.5 h-8 text-sm text-white/90"
                >
                  {tag}
                </div>
              ))}
            </div>
          </div>
        )}

        {editedAudioUrl && (
          <>
            <div className="flex items-center justify-between">
              <div className="text-base text-white">Audio Preview</div>
              <div className="flex bg-white/5 rounded-full p-0.5">
                <button
                  onClick={() => {
                    posthog.capture("Click-OriginalTab-AdvanceEditModal",{is_user_logged_in: true})
                    setShowOriginal(true);
                    setIsEditedAudioReady(false);
                  }}
                  className={`px-2 h-7 text-xs rounded-full ${showOriginal ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'}`}
                >
                  Original
                </button>
                <button
                  onClick={() => {
                    posthog.capture("Click-EditedTab-AdvanceEditModal",{is_user_logged_in: true})
                    setShowOriginal(false)
                  }}
                  className={`px-2 h-7 text-xs rounded-full ${!showOriginal ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'}`}
                >
                  Edited
                </button>
              </div>
            </div>

            <div className="flex w-full">
              <AdvancedEditAudioPlayer
                key={showOriginal ? 'original' : 'edited'}
                audioSrc={showOriginal
                  ? (originalAudioUrl?.webUrl || '')
                  : (editedAudioUrl?.webUrl || originalAudioUrl?.webUrl || '')}
                trackName={showOriginal ? "Original audio" : "Edited audio"}
                highlightRegions={!showOriginal ? waveformChanges : null}
                onReady={(isReady) => {
                  if (!showOriginal && isReady) {
                    setIsEditedAudioReady(true);
                    console.log("[AdvancedEdit] Edited audio is ready for download");
                  }
                }}
              />
            </div>

            {/* File Type selector */}
            <div className="flex items-center gap-3 mt-2">
              <span className="w-22 text-sm text-white">File Type</span>
              <div className="bg-[rgba(255,255,255,0.1)] rounded-lg p-0.5">
                <button
                  onClick={() => setFileType("WAV")}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    fileType === "WAV"
                      ? "bg-[rgba(255,255,255,0.1)] text-white"
                      : "text-[#878188]"
                  }`}
                >
                  WAV
                </button>
                {/* <button
                  onClick={() => setFileType("MP3")}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    fileType === "MP3"
                      ? "bg-[rgba(255,255,255,0.1)] text-white"
                      : "text-[#878188]"
                  }`}
                >
                  MP3
                </button> */}
              </div>
            </div>

            {/* Download button - smaller and centered */}
            <div className="flex gap-2 justify-between mt-2">
              <button
                onClick={resetModalState}
                className="px-3 h-9 flex gap-2 items-center justify-center hover:[background:var(--Color-Opacity-White-5,rgba(255,255,255,0.05))] text-sm text-[#878188] hover:text-white rounded-full"
                id="sign-out-modal-cancel-button"
              >
                <ArrowLeft className="w-4 h-4"/>
              </button>
              <div className={`rounded-full h-9 flex ${!isEditedAudioReady || isDownloading ? 'bg-white/5 text-[#878188] cursor-not-allowed' : 'bg-white text-[#100f10] hover:bg-white/90 transition duration-300'}`}>
                <button
                  className={`flex justify-center items-center px-3 rounded-full text-sm`}
                  onClick={handleDownload}
                  disabled={!isEditedAudioReady || isDownloading}
                >
                  {isDownloading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-t-2 border-black border-t-transparent rounded-full animate-spin"></div>
                      <span>Downloading...</span>
                    </div>
                  ) : (
                    <>
                      <ArrowShapeDownToLine className="w-4 h-4 mr-2" />
                      {isEditedAudioReady ? "Download" : "Loading..."}
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>

    {toastMessage && (
      <Toast
        message={toastMessage}
        type={toastType}
        onClose={() => setToastMessage('')}
      />
    )}
  </div>
  );
};

export default AdvancedEditModal;